import { useKeyboard, UseKeyboardLevel } from "@hubrise/react-components"

import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { nextItemInArray, prevItemInArray } from "shared/utils/javascript"

interface useKeyboardNavigationArgs<T> {
  item: T | undefined
  collection: Array<T>
  indexPath: () => string
  showPath: (t: T) => string
}

const useKeyboardNavigation = <T>({ item, collection, indexPath, showPath }: useKeyboardNavigationArgs<T>): void => {
  const navigate = useNavigateNoUpdate()

  useKeyboard(UseKeyboardLevel.Main, { code: "Escape" }, () => navigate(indexPath()), { description: "goToIndex" })

  useKeyboard(
    UseKeyboardLevel.Main,
    { code: "ArrowUp" },
    () => {
      const prevItem = prevItemInArray(collection, item)
      navigate(prevItem === undefined ? indexPath() : showPath(prevItem))
    },
    { skipIfActiveElementEditable: true, description: "goToPreviousItem" },
  )

  useKeyboard(
    UseKeyboardLevel.Main,
    { code: "ArrowDown" },
    () => {
      const nextItem = nextItemInArray(collection, item)
      navigate(nextItem === undefined ? indexPath() : showPath(nextItem))
    },
    { skipIfActiveElementEditable: true, description: "goToNextItem" },
  )

  useKeyboard(
    UseKeyboardLevel.Main,
    { code: "PageUp" },
    () => {
      let prevItem: T | undefined = item
      for (let i = 0; i < 20 && prevItem !== collection[0]; i++) {
        prevItem = prevItemInArray(collection, prevItem)
      }
      navigate(prevItem === undefined ? indexPath() : showPath(prevItem))
    },
    { skipIfActiveElementEditable: true, description: "goToPreviousPage" },
  )

  useKeyboard(
    UseKeyboardLevel.Main,
    { code: "PageDown" },
    () => {
      let nextItem: T | undefined = item
      for (let i = 0; i < 20 && nextItem !== collection[collection.length - 1]; i++) {
        nextItem = nextItemInArray(collection, nextItem)
      }
      navigate(nextItem === undefined ? indexPath() : showPath(nextItem))
    },
    { skipIfActiveElementEditable: true, description: "goToNextPage" },
  )

  useKeyboard(
    UseKeyboardLevel.Main,
    { code: "Home" },
    () => {
      const firstItem = collection[0] as T | undefined
      navigate(firstItem === undefined ? indexPath() : showPath(firstItem))
    },
    { skipIfActiveElementEditable: true, description: "goToFirstItem" },
  )

  useKeyboard(
    UseKeyboardLevel.Main,
    { code: "End" },
    () => {
      const lastItem = collection[collection.length - 1] as T | undefined
      navigate(lastItem === undefined ? indexPath() : showPath(lastItem))
    },
    { skipIfActiveElementEditable: true, description: "goToLastItem" },
  )
}

export default useKeyboardNavigation
